/**
 * As duas telas de execução por seleção visual (referências em docs/os):
 *
 * - `TilesExecution` — "O que foi trocado?": cartões com ícone (óleos, filtros…) que se
 *   marcam, "Detalhes" do cartão ao lado, resumo da manutenção e rodapé de salvar.
 * - `AxleExecution` — "Selecione o eixo": desenho da carreta com eixos clicáveis,
 *   serviços por eixo e lado, "Repetir em outros eixos" e resumo por eixo.
 *
 * Os dois leem a MESMA configuração do item (posições em lista, campos, ícones) e gravam
 * no mesmo modelo de inspeção: nada aqui conhece óleo, filtro, rolamento ou cubo.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { MaintenanceExecFieldDto, MaintenanceInspectionDto, MaintenanceInspectionPanelDto } from "@bi/types";
import { api, ApiError } from "../../lib/api";
import { fmtNumber } from "../../lib/format";
import { MaintenanceIcon } from "./MaintenanceIcons";
import { INTERVENTION_LABEL } from "./work-order-inspection-ui";

export function useExecutionPanel(workOrderId: string, itemId: string, onMudou: () => void) {
  const qc = useQueryClient();
  const chave = ["maintenance", "inspection-panel", workOrderId, itemId];
  const base = `/maintenance/work-orders/${workOrderId}/checklist-items/${itemId}/inspection`;
  const painel = useQuery({ queryKey: chave, queryFn: () => api.get<MaintenanceInspectionPanelDto>(base) });
  const [erro, setErro] = useState<string | null>(null);
  const ok = () => { setErro(null); qc.invalidateQueries({ queryKey: chave }); onMudou(); };
  const falha = (e: unknown) => setErro(e instanceof ApiError ? e.message : "Não foi possível salvar.");
  const salvar = useMutation({
    mutationFn: (v: { positionKey: string; measures: Record<string, unknown>; notes: string | null }) => {
      const cfg = painel.data!.config;
      return api.put(base, {
        positionKey: v.positionKey, condition: "ok", interventions: [cfg.implicitIntervention ?? (cfg.layout === "tiles" ? "replaced" : "other")],
        measures: Object.fromEntries(Object.entries(v.measures).filter(([, x]) => x !== "" && x !== undefined && !(Array.isArray(x) && x.length === 0))),
        notes: v.notes,
      });
    },
    onSuccess: ok, onError: falha,
  });
  const limpar = useMutation({
    mutationFn: (positionKey: string) => api.del(`${base}?positionKey=${encodeURIComponent(positionKey)}`),
    onSuccess: ok, onError: falha,
  });
  return { painel, salvar, limpar, erro, setErro };
}

/**
 * Uso AVULSO (Central → Nova manutenção): sem o painel da O.S. em volta, a tela leva o
 * próprio título, não tem "Fechar" no topo e o rodapé salva a manutenção inteira.
 */
export interface ExecucaoAvulsa { titulo: string; salvando: boolean; onSalvar: () => void }

const fmtDia = (d: Date) => d.toLocaleDateString("pt-BR");

/** Faixa de contexto do topo, como nas referências: placa, quilometragem, data e responsável. */
function Faixa({ p, responsavel }: { p: MaintenanceInspectionPanelDto; responsavel: string | null }) {
  const itens: Array<[string, string]> = [
    ["Placa", p.workOrder.vehiclePlate],
    ["Quilometragem", p.workOrder.odometerKm != null ? `${fmtNumber(p.workOrder.odometerKm)} km` : "—"],
    ["Data", fmtDia(new Date())],
    ["Responsável", responsavel ?? "—"],
  ];
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-2 rounded-xl border border-surface-border bg-white px-4 py-3 sm:grid-cols-4">
      {itens.map(([r, v]) => (
        <div key={r}><dt className="text-xs text-gray-500">{r}</dt><dd className="font-semibold text-gray-900">{v}</dd></div>
      ))}
    </dl>
  );
}

function Cabecalho({ titulo, sub, p, responsavel, onFechar, avulsa }: { titulo: string; sub: string; p: MaintenanceInspectionPanelDto; responsavel: string | null; onFechar: () => void; avulsa?: boolean }) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-xl font-semibold text-gray-900">{titulo}</h3>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${p.item.status === "done" ? "bg-tm-50 text-tm-700" : "bg-warning-100 text-warning-700"}`}>
              {p.item.status === "done" ? "Concluído" : "Rascunho"}
            </span>
          </div>
          <p className="text-sm text-gray-500">{sub}</p>
        </div>
        {!avulsa && <button type="button" className="btn-ghost text-sm" onClick={onFechar}>✕ Fechar</button>}
      </div>
      <Faixa p={p} responsavel={responsavel} />
    </div>
  );
}

function Rodape({ texto, onFechar, onSalvar, desabilitado, rotuloSalvar }: { texto: string; onFechar: () => void; onSalvar: () => void; desabilitado: boolean; rotuloSalvar: string }) {
  return (
    <div className="sticky bottom-0 z-10 -mx-1 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-surface-border bg-white px-4 py-3 shadow-sm">
      <span className="text-sm text-gray-600">{texto}</span>
      <span className="flex gap-2">
        <button type="button" className="btn-secondary min-h-[2.75rem]" onClick={onFechar}>Cancelar</button>
        <button type="button" className="btn-primary min-h-[2.75rem]" disabled={desabilitado} onClick={onSalvar}>✓ {rotuloSalvar}</button>
      </span>
    </div>
  );
}

const CardPasso = ({ n, titulo, sub, extra, children }: { n: number; titulo: string; sub?: string; extra?: React.ReactNode; children: React.ReactNode }) => (
  <section className="rounded-xl border border-surface-border bg-white p-4">
    <div className="mb-3 flex flex-wrap items-center gap-2">
      <h4 className="text-lg font-semibold text-gray-900">{n}. {titulo}</h4>
      {extra}
    </div>
    {sub && <p className="-mt-2 mb-3 text-sm text-gray-500">{sub}</p>}
    {children}
  </section>
);

function CampoSimples({ f, valor, onChange, disabled }: { f: MaintenanceExecFieldDto; valor: unknown; onChange: (v: unknown) => void; disabled: boolean }) {
  const rotulo = `${f.label}${f.unit ? ` (${f.unit})` : ""}${f.required ? " *" : ""}`;
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-gray-800">{rotulo}</span>
      <input
        className="input w-full" disabled={disabled} maxLength={200}
        type={f.type === "date" ? "date" : "text"} inputMode={f.type === "number" || f.type === "measurement" ? "decimal" : undefined}
        placeholder={f.type === "date" ? "dd/mm/aaaa" : "Informar"}
        value={valor == null ? "" : String(valor)}
        onChange={(e) => onChange(f.type === "number" || f.type === "measurement" ? e.target.value.replace(",", ".") : e.target.value)}
      />
    </label>
  );
}

// ── "O que foi trocado?" ────────────────────────────────────────────────────

export function TilesExecution({
  workOrderId, itemId, podeEditar, responsavel, onFechar, onMudou, avulsa,
}: { workOrderId: string; itemId: string; podeEditar: boolean; responsavel: string | null; onFechar: () => void; onMudou: () => void; avulsa?: ExecucaoAvulsa }) {
  const { painel, salvar, limpar, erro, setErro } = useExecutionPanel(workOrderId, itemId, onMudou);
  const p = painel.data;
  // marcados na tela (inclui os que ainda faltam detalhes) × gravados no servidor
  const [marcados, setMarcados] = useState<Set<string> | null>(null);
  const [sel, setSel] = useState<string | null>(null);
  const [form, setForm] = useState<{ measures: Record<string, unknown>; notes: string }>({ measures: {}, notes: "" });

  const gravadas = useMemo(() => new Map((p?.inspections ?? []).map((i) => [i.positionKey, i])), [p]);
  const marcadosAtual = marcados ?? new Set(gravadas.keys());
  const primeiroMarcado = p?.positions.find((x) => marcadosAtual.has(x.key))?.key ?? null;
  if (p && sel === null && primeiroMarcado) queueMicrotask(() => abrir(primeiroMarcado, gravadas.get(primeiroMarcado)));
  if (!p) return <div className="rounded-xl border border-surface-border p-4 text-sm text-gray-500">Carregando…</div>;

  const editavel = podeEditar && p.workOrder.status !== "completed" && p.workOrder.status !== "cancelled";
  const grupos = new Map<string, typeof p.positions>();
  for (const x of p.positions) grupos.set(x.group ?? "", [...(grupos.get(x.group ?? "") ?? []), x]);

  function abrir(key: string, g: MaintenanceInspectionDto | undefined) {
    setSel(key);
    setForm({ measures: { ...(g?.measures ?? {}) }, notes: g?.notes ?? "" });
    setErro(null);
  }
  const alternar = (key: string) => {
    if (!editavel) { abrir(key, gravadas.get(key)); return; }
    const novo = new Set(marcadosAtual);
    if (novo.has(key)) {
      novo.delete(key);
      setMarcados(novo);
      if (gravadas.has(key)) limpar.mutate(key);
      if (sel === key) setSel(null);
    } else {
      novo.add(key);
      setMarcados(novo);
      abrir(key, gravadas.get(key));
    }
  };

  const faltam = [...marcadosAtual].filter((k) => !gravadas.has(k));
  const pos = p.positions.find((x) => x.key === sel);
  const total = marcadosAtual.size;

  return (
    <div className="space-y-4" aria-label={p.item.itemName}>
      <Cabecalho titulo={avulsa?.titulo ?? p.item.itemName} sub="Marque os itens trocados e registre os detalhes." p={p} responsavel={responsavel} onFechar={onFechar} avulsa={!!avulsa} />
      <div className="grid gap-4 lg:grid-cols-5">
        <div className="lg:col-span-3">
          <CardPasso n={1} titulo={p.config.implicitIntervention === "replaced" ? "O que foi trocado?" : "O que foi feito?"} sub="Você pode selecionar vários itens.">
            {[...grupos.entries()].map(([g, itens]) => (
              <div key={g} className="mb-3">
                {g && <p className="mb-1.5 text-sm font-semibold text-gray-800">{g}</p>}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {itens.map((x) => {
                    const on = marcadosAtual.has(x.key);
                    const salvo = gravadas.has(x.key);
                    return (
                      <button
                        key={x.key} type="button" aria-pressed={on} onClick={() => alternar(x.key)}
                        className={`relative flex min-h-[8rem] flex-col items-center justify-center gap-2 rounded-xl border-2 p-3 text-center transition ${on ? "border-tm-500 bg-tm-50" : "border-surface-border bg-white hover:border-tm-300"} ${sel === x.key ? "ring-2 ring-tm-500 ring-offset-1" : ""}`}
                      >
                        <span className={`absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded border ${on ? "border-tm-600 bg-tm-600 text-white" : "border-gray-300 bg-white"}`} aria-hidden>
                          {on && <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M3.5 8.5l3 3 6-7" /></svg>}
                        </span>
                        <MaintenanceIcon ilustrado name={x.icon} className={`h-20 w-32 ${on ? "text-tm-700" : "text-gray-500"}`} />
                        <span className="text-sm font-medium text-gray-900">{x.label}</span>
                        {on && !salvo && <span className="text-[0.6875rem] text-warning-700">Falta preencher os detalhes</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </CardPasso>
        </div>

        <div className="lg:col-span-2">
          <CardPasso n={2} titulo="Detalhes do item" sub="Preencha os detalhes de cada item marcado.">
            {marcadosAtual.size === 0 ? (
              <p className="rounded-lg border border-dashed border-surface-border p-4 text-sm text-gray-500">Marque um item ao lado para registrar os detalhes.</p>
            ) : (
              <div className="space-y-3">
                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-gray-800">Item selecionado</span>
                  <select className="input w-full" value={sel ?? ""} onChange={(e) => abrir(e.target.value, gravadas.get(e.target.value))}>
                    <option value="" disabled>Escolha…</option>
                    {p.positions.filter((x) => marcadosAtual.has(x.key)).map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
                  </select>
                </label>
                {sel && pos && (
                  <>
                    {p.config.fields.map((f) => (
                      <CampoSimples key={f.name} f={f} disabled={!editavel} valor={form.measures[f.name]}
                        onChange={(v) => setForm((s) => ({ ...s, measures: { ...s.measures, [f.name]: v } }))} />
                    ))}
                    <label className="block">
                      <span className="mb-1 block text-sm font-medium text-gray-800">Observação</span>
                      <textarea className="input w-full" rows={2} maxLength={1000} disabled={!editavel} value={form.notes}
                        onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))} />
                    </label>
                    {editavel && (
                      <button type="button" className="btn-primary w-full min-h-[2.75rem]" disabled={salvar.isPending}
                        onClick={() => salvar.mutate({ positionKey: sel, measures: form.measures, notes: form.notes.trim() || null })}>
                        {salvar.isPending ? "Salvando…" : gravadas.has(sel) ? "Atualizar item" : "Salvar item"}
                      </button>
                    )}
                  </>
                )}
                <div className="flex items-start gap-3 rounded-lg bg-tm-50 p-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-tm-600 text-white" aria-hidden>✓</span>
                  <div className="text-sm">
                    <p className="font-semibold text-tm-800">{total} {total === 1 ? "item marcado" : "itens marcados"}</p>
                    <p className="text-gray-600">{faltam.length ? `Preencha os detalhes de cada item marcado para concluir (faltam ${faltam.length}).` : "Todos os detalhes foram preenchidos."}</p>
                  </div>
                </div>
              </div>
            )}
            {erro && <p className="mt-2 rounded-lg border border-danger-600/30 bg-danger-50 p-2 text-sm text-danger-600">{erro}</p>}
          </CardPasso>
        </div>
      </div>

      <section className="rounded-xl border border-surface-border bg-white p-4">
        <h4 className="mb-2 text-lg font-semibold text-gray-900">Resumo da manutenção</h4>
        {gravadas.size === 0 ? <p className="text-sm text-gray-500">Nenhum item salvo ainda.</p> : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {p.positions.filter((x) => gravadas.has(x.key)).map((x) => (
              <button key={x.key} type="button" onClick={() => abrir(x.key, gravadas.get(x.key))}
                className="flex items-center justify-between gap-2 rounded-lg border border-surface-border px-3 py-2 text-left hover:bg-surface-muted">
                <span className="flex min-w-0 items-center gap-2"><MaintenanceIcon ilustrado name={x.icon} className="h-8 w-12 shrink-0 text-gray-500" /><span className="truncate text-sm text-gray-900">{x.label}</span></span>
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-tm-600 text-white" aria-hidden>
                  <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M3.5 8.5l3 3 6-7" /></svg>
                </span>
              </button>
            ))}
          </div>
        )}
        <p className="mt-2 text-xs text-gray-500">Confira os itens e os detalhes antes de salvar.</p>
      </section>

      <Rodape
        texto={`${total} ${total === 1 ? "item selecionado" : "itens selecionados"}`}
        onFechar={onFechar} onSalvar={avulsa ? avulsa.onSalvar : onFechar}
        desabilitado={(editavel && faltam.length > 0) || (!!avulsa && (avulsa.salvando || total === 0))}
        rotuloSalvar={avulsa ? "Salvar manutenção" : "Concluir registro"}
      />
    </div>
  );
}

// ── "Selecione o eixo" ──────────────────────────────────────────────────────

/** "Eixo 3 · tração" → "Eixo 3": o tipo do eixo aparece à parte, em texto próprio. */
const nomeCurto = (l: string) => l.split(" · ")[0]!;

export const TIPO_EIXO: Record<string, string> = { steering: "Direcional", traction: "Tração", free: "Livre", lift: "Auxiliar suspenso" };

interface EixoDesenho { key: string; label: string; axleType?: string | null; dual?: boolean }

/**
 * A carreta do cliente (referência em docs/os): topo + uma peça por eixo + base, recortadas da
 * imagem. O eixo selecionado usa a peça verde; os demais, a preta. Rótulo e marca à direita.
 */
function CarretaIlustrada({
  eixos, selecionado, comServico, onSelect,
}: { eixos: EixoDesenho[]; selecionado: string | null; comServico: Set<string>; onSelect: (k: string) => void }) {
  const base = "/manutencao-carreta";
  return (
    <div className="mx-auto w-full max-w-[26.25rem]" role="group" aria-label="Carreta vista de cima">
      <div className="w-[16.875rem] text-center">
        <div className="text-base leading-none text-gray-700" aria-hidden="true">↑</div>
        <div className="text-xs font-semibold tracking-wide text-gray-700">FRENTE</div>
      </div>
      <img src={`${base}/topo.png`} alt="" width={270} height={56} className="block" draggable={false} />
      {eixos.map((e) => {
        const ativo = selecionado === e.key;
        const marcado = comServico.has(e.key);
        return (
          <div
            key={e.key} role="button" tabIndex={0} aria-pressed={ativo} className="flex cursor-pointer items-center outline-none focus-visible:ring-2 focus-visible:ring-tm-500"
            aria-label={`${e.label}${e.axleType ? `, ${TIPO_EIXO[e.axleType] ?? e.axleType}` : ""}, rodas duplas${marcado ? ", com serviços" : ""}`}
            onClick={() => onSelect(e.key)}
            onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); onSelect(e.key); } }}
          >
            <img src={`${base}/${ativo ? "eixo-verde" : "eixo-preto"}.png`} alt="" width={270} height={84} className="block shrink-0" draggable={false} />
            <div className="ml-1 flex flex-1 items-center justify-between gap-3">
              <span className={`rounded-md px-2 py-0.5 text-[0.9375rem] font-semibold ${ativo ? "bg-tm-50 text-gray-900" : "text-gray-900"}`}>
                {nomeCurto(e.label)}
                {e.axleType && <span className="ml-1.5 text-xs font-normal text-gray-500">{TIPO_EIXO[e.axleType] ?? e.axleType}</span>}
              </span>
              <span
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-sm leading-none text-white ${ativo ? "bg-tm-600" : marcado ? "bg-gray-500" : "border border-gray-400 bg-white"}`}
                aria-hidden="true"
              >
                {(ativo || marcado) && "✓"}
              </span>
            </div>
          </div>
        );
      })}
      <img src={`${base}/base.png`} alt="" width={270} height={20} className="block" draggable={false} />
    </div>
  );
}

/**
 * O veículo visto de cima, montado a partir dos EIXOS REAIS dele: direcional (rodas simples,
 * mais finas), tração e livre (rodas duplas quando é o caso). Com eixo direcional aparece a
 * cabine — é cavalo; sem ele, é a carreta. Cada eixo é clicável.
 */
function Carreta({
  eixos, selecionado, comServico, onSelect,
}: { eixos: EixoDesenho[]; selecionado: string | null; comServico: Set<string>; onSelect: (k: string) => void }) {
  const alturaEixo = 86;
  const cabine = eixos.some((e) => e.axleType === "steering");
  // carreta de rodas duplas: o desenho do cliente, montado por eixo (peças recortadas da referência)
  if (!cabine && eixos.length > 0 && eixos.every((e) => e.dual)) {
    return <CarretaIlustrada eixos={eixos} selecionado={selecionado} comServico={comServico} onSelect={onSelect} />;
  }
  const topo = cabine ? 78 : 34;
  const h = topo + 22 + eixos.length * alturaEixo;
  return (
    <svg viewBox={`0 0 390 ${h}`} className="mx-auto h-auto w-full max-w-[24.375rem]" role="group" aria-label={cabine ? "Cavalo visto de cima" : "Carreta vista de cima"}>
      <text x="120" y="12" textAnchor="middle" fontSize="11" fontWeight="600" fill="#374151">FRENTE</text>
      <path d="M120 22l-5 7h10z" fill="#374151" />
      {cabine && (
        <g>
          <rect x="92" y="34" width="56" height="34" rx="9" fill="#e5e7eb" stroke="#9ca3af" />
          <text x="120" y="55" textAnchor="middle" fontSize="10" fill="#6b7280">Cabine</text>
        </g>
      )}
      <rect x="86" y={topo} width="68" height={h - topo - 8} rx="5" fill="#f3f4f6" stroke="#9ca3af" />
      <path d={`M104 ${topo}v${h - topo - 8}M136 ${topo}v${h - topo - 8}`} stroke="#d1d5db" />
      {eixos.map((e, i) => {
        const y = topo + 28 + i * alturaEixo;
        const ativo = selecionado === e.key;
        const cor = ativo ? "var(--brand)" : "#1f2937";
        const fino = e.axleType === "steering";
        const larg = fino ? 14 : 18;
        const rodas = (xs: number[]) => xs.map((x) => <rect key={x} x={x} y={y - 18} width={larg} height="36" rx="4" fill={cor} />);
        return (
          <g key={e.key} role="button" tabIndex={0} aria-pressed={ativo}
            aria-label={`${e.label}${e.axleType ? `, ${TIPO_EIXO[e.axleType] ?? e.axleType}` : ""}${e.dual ? ", rodas duplas" : ""}${comServico.has(e.key) ? ", com serviços" : ""}`}
            className="cursor-pointer outline-none" onClick={() => onSelect(e.key)}
            onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); onSelect(e.key); } }}>
            <rect x="40" y={y - 22} width="160" height="44" fill="transparent" />
            {e.dual ? rodas([46, 66, 176, 194]) : rodas([62, 172])}
            <rect x="84" y={y - 5} width="72" height="10" rx="3" fill={ativo ? "var(--brand)" : "#9ca3af"} />
            <circle cx="120" cy={y} r="8" fill="#e5e7eb" stroke={ativo ? "var(--brand)" : "#6b7280"} strokeWidth="2" />
            <text x="222" y={e.axleType ? y - 2 : y + 5} fontSize="14" fontWeight="600" fill="#111827">{nomeCurto(e.label)}</text>
            {e.axleType && <text x="222" y={y + 13} fontSize="11" fill="#6b7280">{TIPO_EIXO[e.axleType] ?? e.axleType}{e.dual ? " · rodas duplas" : ""}</text>}
            <circle cx="366" cy={y} r="11" fill={ativo ? "var(--brand)" : comServico.has(e.key) ? "#6b7280" : "#fff"} stroke={ativo || comServico.has(e.key) ? "none" : "#9ca3af"} strokeWidth="1.5" />
            {(ativo || comServico.has(e.key)) && <path d={`M360 ${y}l4 4 8-8`} fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />}
          </g>
        );
      })}
    </svg>
  );
}

export function AxleExecution({
  workOrderId, itemId, podeEditar, responsavel, onFechar, onMudou, avulsa,
}: { workOrderId: string; itemId: string; podeEditar: boolean; responsavel: string | null; onFechar: () => void; onMudou: () => void; avulsa?: ExecucaoAvulsa }) {
  const { painel, salvar, limpar, erro, setErro } = useExecutionPanel(workOrderId, itemId, onMudou);
  const p = painel.data;
  const [sel, setSel] = useState<string | null>(null);
  const [form, setForm] = useState<{ measures: Record<string, unknown>; notes: string }>({ measures: {}, notes: "" });
  const [repetindo, setRepetindo] = useState<Set<string> | null>(null);

  const gravadas = useMemo(() => new Map((p?.inspections ?? []).map((i) => [i.positionKey, i])), [p]);
  const eixos = p?.positions ?? [];
  const atual = sel ?? eixos[0]?.key ?? null;
  const ladoField = p?.config.fields.find((f) => f.type === "choice");
  const servicosField = p?.config.fields.find((f) => f.type === "multichoice");

  // ao trocar de eixo, carrega o que está gravado nele
  const abrir = (key: string) => {
    const g = gravadas.get(key);
    setSel(key); setRepetindo(null); setErro(null);
    setForm({ measures: { ...(g?.measures ?? (ladoField ? { [ladoField.name]: ladoField.options?.[0]?.value } : {})) }, notes: g?.notes ?? "" });
  };
  if (!p) return <div className="rounded-xl border border-surface-border p-4 text-sm text-gray-500">Carregando…</div>;
  if (sel === null && atual) queueMicrotask(() => abrir(atual));

  const editavel = podeEditar && p.workOrder.status !== "completed" && p.workOrder.status !== "cancelled";
  const servicos = servicosField ? (Array.isArray(form.measures[servicosField.name]) ? (form.measures[servicosField.name] as string[]) : []) : [];
  const eixo = eixos.find((x) => x.key === atual);
  const comServico = new Set(gravadas.keys());
  const rotuloLado = (v: unknown) => ladoField?.options?.find((o) => o.value === v)?.label ?? "";
  const rotuloServico = (v: string) => servicosField?.options?.find((o) => o.value === v)?.label ?? v;

  const gravar = (key: string, medidas: Record<string, unknown>) => {
    const lista = servicosField && Array.isArray(medidas[servicosField.name]) ? (medidas[servicosField.name] as string[]) : [];
    if (servicosField && lista.length === 0) return limpar.mutate(key); // sem serviço, o eixo não entra
    return salvar.mutateAsync({ positionKey: key, measures: medidas, notes: form.notes.trim() || null });
  };

  return (
    <div className="space-y-4" aria-label={p.item.itemName}>
      <Cabecalho titulo={avulsa?.titulo ?? p.item.itemName} sub="Selecione o eixo e registre os serviços realizados." p={p} responsavel={responsavel} onFechar={onFechar} avulsa={!!avulsa} />
      <div className="grid gap-4 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <CardPasso n={1} titulo="Selecione o eixo" sub="Toque no desenho da carreta para selecionar o eixo.">
            {eixos.length === 0
              ? <p className="rounded-lg border border-dashed border-surface-border p-4 text-sm text-gray-500">Este veículo ainda não tem a configuração de eixos cadastrada. Cadastre em Pneus para detalhar o serviço por eixo.</p>
              : <Carreta eixos={eixos.map((x) => ({ key: x.key, label: x.label, axleType: x.axleType, dual: x.dual }))} selecionado={atual} comServico={comServico} onSelect={abrir} />}
            <div className="mt-2 flex flex-wrap justify-center gap-4 text-xs text-gray-600">
              <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-full bg-tm-500" /> Selecionado</span>
              <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-full bg-gray-500" /> Com serviços</span>
              <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-full border border-gray-400" /> Sem serviços</span>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {eixos.map((x) => (
                <button key={x.key} type="button" onClick={() => abrir(x.key)} aria-pressed={atual === x.key}
                  className={`min-h-[2.75rem] rounded-lg border text-sm font-medium ${atual === x.key ? "border-tm-600 bg-tm-600 text-white" : "border-surface-border text-gray-800"}`}>
                  {nomeCurto(x.label)}{comServico.has(x.key) && atual !== x.key ? " ✓" : ""}
                </button>
              ))}
            </div>
          </CardPasso>
        </div>

        <div className="lg:col-span-3">
          <CardPasso n={2} titulo={`Serviços do ${eixo ? nomeCurto(eixo.label) : "eixo"}`} extra={<><span className="rounded-full bg-tm-50 px-2.5 py-0.5 text-xs font-medium text-tm-700">Eixo selecionado</span>{eixo?.axleType && <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">{TIPO_EIXO[eixo.axleType] ?? eixo.axleType}{eixo.dual ? " · rodas duplas" : ""}</span>}</>}>
            {ladoField && (
              <div className="mb-3">
                <p className="mb-1 text-sm font-medium text-gray-800">Onde o serviço foi realizado?</p>
                <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Lado">
                  {ladoField.options?.map((o) => {
                    const on = form.measures[ladoField.name] === o.value;
                    return (
                      <button key={o.value} type="button" role="radio" aria-checked={on} disabled={!editavel}
                        onClick={() => setForm((s) => ({ ...s, measures: { ...s.measures, [ladoField.name]: o.value } }))}
                        className={`min-h-[2.75rem] rounded-lg border text-sm font-medium ${on ? "border-tm-600 bg-tm-600 text-white" : "border-surface-border text-gray-800"}`}>{o.label}</button>
                    );
                  })}
                </div>
              </div>
            )}
            {servicosField && (
              <div className="grid gap-2 sm:grid-cols-2">
                {servicosField.options?.filter((o) => !o.axleTypes?.length || !eixo?.axleType || o.axleTypes.includes(eixo.axleType)).map((o) => {
                  const on = servicos.includes(o.value);
                  return (
                    <button key={o.value} type="button" aria-pressed={on} disabled={!editavel}
                      onClick={() => setForm((s) => ({ ...s, measures: { ...s.measures, [servicosField.name]: on ? servicos.filter((x) => x !== o.value) : [...servicos, o.value] } }))}
                      className={`flex min-h-[4rem] items-center gap-3 rounded-xl border-2 px-3 text-left ${on ? "border-tm-500 bg-tm-50" : "border-surface-border bg-white"}`}>
                      <MaintenanceIcon ilustrado name={o.icon} className={`h-12 w-14 shrink-0 ${on ? "text-tm-700" : "text-gray-500"}`} />
                      <span className="flex-1 text-sm font-medium text-gray-900">{o.label}</span>
                      <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${on ? "border-tm-600 bg-tm-600 text-white" : "border-gray-300 bg-white"}`} aria-hidden>
                        {on && <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M3.5 8.5l3 3 6-7" /></svg>}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            {p.config.fields.filter((f) => f !== ladoField && f !== servicosField).map((f) => (
              <div key={f.name} className="mt-3">
                <CampoSimples f={f} disabled={!editavel} valor={form.measures[f.name]} onChange={(v) => setForm((s) => ({ ...s, measures: { ...s.measures, [f.name]: v } }))} />
              </div>
            ))}

            {editavel && (
              <div className="mt-3 space-y-2">
                <button type="button" className="btn-secondary inline-flex min-h-[2.75rem] items-center gap-2" disabled={eixos.length < 2 || servicos.length === 0}
                  onClick={() => setRepetindo(repetindo ? null : new Set(eixos.filter((x) => x.key !== atual).map((x) => x.key)))}>
                  ⧉ Repetir em outros eixos
                </button>
                {repetindo && (
                  <div className="rounded-lg border border-surface-border bg-surface-muted p-3">
                    <p className="mb-2 text-sm text-gray-700">Copiar estes serviços e o lado para:</p>
                    <div className="flex flex-wrap gap-3">
                      {eixos.filter((x) => x.key !== atual).map((x) => (
                        <label key={x.key} className="inline-flex items-center gap-1.5 text-sm">
                          <input type="checkbox" checked={repetindo.has(x.key)}
                            onChange={(e) => setRepetindo((s) => { const n = new Set(s); if (e.target.checked) n.add(x.key); else n.delete(x.key); return n; })} /> {x.label}
                        </label>
                      ))}
                    </div>
                    <button type="button" className="btn-primary mt-2" disabled={salvar.isPending || repetindo.size === 0}
                      onClick={async () => { for (const k of repetindo) await gravar(k, form.measures); setRepetindo(null); }}>
                      Copiar
                    </button>
                  </div>
                )}
              </div>
            )}

            <label className="mt-3 block">
              <span className="mb-1 block text-sm font-medium text-gray-800">Observações</span>
              <textarea className="input w-full" rows={3} maxLength={1000} disabled={!editavel} value={form.notes}
                placeholder="Descreva peças utilizadas ou detalhes do serviço…" onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))} />
            </label>
            {erro && <p className="mt-2 rounded-lg border border-danger-600/30 bg-danger-50 p-2 text-sm text-danger-600">{erro}</p>}
            {editavel && atual && (
              <button type="button" className="btn-primary mt-3 min-h-[2.75rem]" disabled={salvar.isPending || limpar.isPending}
                onClick={() => gravar(atual, form.measures)}>
                {salvar.isPending ? "Salvando…" : servicos.length === 0 && gravadas.has(atual) ? "Remover serviços do eixo" : "Salvar eixo"}
              </button>
            )}
          </CardPasso>
        </div>
      </div>

      <section className="rounded-xl border border-surface-border bg-white p-4">
        <h4 className="text-lg font-semibold text-gray-900">Resumo da manutenção</h4>
        <p className="mb-2 text-sm text-gray-500">Confira os serviços selecionados por eixo.</p>
        <ul className="divide-y divide-gray-100">
          {eixos.map((x) => {
            const g = gravadas.get(x.key);
            const lista = servicosField && g && Array.isArray(g.measures?.[servicosField.name]) ? (g.measures![servicosField.name] as string[]) : [];
            return (
              <li key={x.key}>
                <button type="button" onClick={() => abrir(x.key)} className="flex w-full flex-wrap items-center gap-2 py-2 text-left">
                  <span className="w-20 font-semibold text-gray-900">{nomeCurto(x.label)}{x.axleType && <span className="block text-[0.625rem] font-normal text-gray-400">{TIPO_EIXO[x.axleType] ?? x.axleType}</span>}</span>
                  <span className="text-sm text-gray-500">• {g ? rotuloLado(g.measures?.[ladoField?.name ?? ""]) || "Registrado" : "Sem serviços"}</span>
                  <span className="flex flex-1 flex-wrap gap-1.5">
                    {lista.map((s) => <span key={s} className="rounded bg-tm-50 px-2 py-0.5 text-xs font-medium text-tm-800">{rotuloServico(s).replace(/^(Troca de|Aplicação de|Inspeção do|Revisão de) /, "").replace(/^./, (c) => c.toUpperCase())}</span>)}
                    {!g && <span className="text-gray-400">—</span>}
                  </span>
                  <span className="text-gray-400" aria-hidden>›</span>
                </button>
              </li>
            );
          })}
        </ul>
      </section>

      <Rodape texto="Revise os serviços antes de salvar." onFechar={onFechar} onSalvar={avulsa ? avulsa.onSalvar : onFechar} desabilitado={!!avulsa && (avulsa.salvando || comServico.size === 0)} rotuloSalvar={avulsa ? "Salvar manutenção" : "Concluir registro"} />
    </div>
  );
}

export const ROTULO_INTERVENCAO = INTERVENTION_LABEL;
